import { chain, filter, map, omit, reduce } from 'lodash';

import baseIvrsResource from 'src/actions/ivrs/baseIvrsResource';
import { DomainError as StandardDomainError, Unauthorized } from '@ad-scripts/application/lib/error';
import { logout } from 'src/actions/auth/authSlice';
import Thread, { IVR_TAGS, Node } from 'src/models/Thread/Thread';
import ThreadMenuNode from 'src/models/Thread/Nodes/ThreadMenuNodeDef';
import { toastError, toastSuccess } from 'src/actions/toast';
import { translateErrors } from 'src/adapters/validateType';
import validateType from 'src/adapters/validateType';

const threadsResource = baseIvrsResource<Thread>({
  name: 'threads',
  endpoint: 'v1/orgs/:orgId/consumer-self-service/ivrs/:id',
  type: Thread,
  options: { versionedUpdates: true },
});

const { fetchStarted, fetchSuccess, fetchError, fetchRequestError } = threadsResource.actions;

threadsResource.effects.fetchAll =
  (params: any) =>
  async (dispatch, getState, { gateways }) => {
    try {
      dispatch(fetchStarted({}));
      const result = await gateways.threads.requestAll(params, {
        tag: [IVR_TAGS.WISMO, IVR_TAGS.CONSUMER_SELF_SERVICE],
      });
      dispatch(fetchSuccess(result));
    } catch (err) {
      if (err instanceof StandardDomainError) {
        dispatch(fetchError(translateErrors(err.errors)));
      } else if (err instanceof Unauthorized) {
        dispatch(logout());
      } else {
        dispatch(fetchRequestError({}));
      }
    }
  };

threadsResource.effects.validateThread = (thread: Partial<Thread & { nodesMap: Record<string, Node> }>) => dispatch => {
  const nodes = map(thread.nodesMap, node => node) as Node[];
  const threadConfig = { ...omit(thread, ['nodesMap']), nodes };

  try {
    validateType(Thread, threadConfig);
  } catch (err) {
    const transformedErrors = transformErrors(threadConfig, err);

    dispatch(threadsResource.actions.validationError(transformedErrors));

    const name = thread.name ? `"${thread.name}"` : 'the Thread';

    let toastMessage = `Unable to ${
      thread.id ? 'update' : 'create'
    } ${name}. Please make sure you have all the messages filled out.`;

    if (transformedErrors.length === 1 && transformedErrors[0].code === 'isPropertyAlreadyExists') {
      toastMessage = `${name} already exists. Please use a new name.`;
    }

    dispatch(toastError(toastMessage));
    return false;
  }
  return true;
};

const transformErrors = (threadConfig, err) => [
  ...filter(err?.errors, ({ attr }) => !attr?.startsWith('nodes[')).map(err => ({ ...err })),
  ...reduce(
    threadConfig.nodes,
    (acc: any[], node, index) => {
      const list = filter(err?.errors, ({ attr }) => attr.startsWith(`nodes[${index}].`));

      const nodeErrors = reduce(list, (acc: any, error) => [...acc, ...toTransformedError(node, error, index)], []);

      return [...acc, ...nodeErrors];
    },
    []
  ),
];

const ERROR_DETAILS_MAP = {
  ['Enter a message']: 'Please enter a message',
  ['label should not be empty']: 'Please add a label',
};

const toTransformedError = (node: Node, error, index) => {
  const attr = `nodesMap.${node.ivrNode.id}.${error.attr.replace(`nodes[${index}].`, '')}`;

  const errorMessage = ERROR_DETAILS_MAP[error.detail] ? ERROR_DETAILS_MAP[error.detail] : error.detail;

  if (error.code === 'MenuLabelIsUnique') {
    const menuNode = node.ivrNode as ThreadMenuNode;

    const duplicates = Object.keys(
      chain(menuNode.menu)
        .groupBy('label')
        .pickBy(x => x.length > 1)
        .value()
    );

    return reduce(
      menuNode.menu,
      (acc: any, menu, index) => {
        if (duplicates.includes(menu.label)) {
          acc.push({ ...error, attr: `${attr}[${index}].label`, detail: errorMessage });
        }

        return acc;
      },
      []
    );
  }

  return [{ ...error, attr, detail: errorMessage }];
};

threadsResource.effects.add =
  (thread: Thread) =>
  async (dispatch, getState, { gateways }) => {
    try {
      const auth = getState().auth;
      dispatch(threadsResource.actions.addStarted({}));

      await gateways[threadsResource.name].addIvr(thread);
      await gateways[threadsResource.name].promote({ id: thread.id, version: thread.version }, thread);

      dispatch(toastSuccess(`"${thread.name}" has been created.`));
      dispatch(
        threadsResource.actions.addSuccess({
          ...thread,
          publishedAt: new Date().toISOString(),
          publishedBy: auth.userId,
        })
      );
    } catch (err) {
      if (err instanceof StandardDomainError) {
        dispatch(threadsResource.actions.addError(translateErrors(err.errors)));
      } else if (err instanceof Unauthorized) {
        dispatch(logout());
      } else {
        dispatch(threadsResource.actions.addRequestError(err));
      }
      dispatch(toastError(`There was an error while adding`));
    }
  };

threadsResource.effects.createNewVersion =
  (params: any, thread: Thread) =>
  async (dispatch, getState, { gateways }) => {
    try {
      const auth = getState().auth;
      dispatch(threadsResource.actions.addStarted({}));

      const response = await gateways[threadsResource.name].addVersion(params, thread);
      await gateways[threadsResource.name].promote({ id: response.id, version: response.version }, thread);

      dispatch(toastSuccess(`"${thread.name}" has been updated.`));
      dispatch(
        threadsResource.actions.addSuccess({
          ...response,
          publishedAt: new Date().toISOString(),
          publishedBy: auth.userId,
        })
      );
    } catch (err) {
      if (err instanceof StandardDomainError) {
        dispatch(threadsResource.actions.addError(translateErrors(err.errors)));
      } else if (err instanceof Unauthorized) {
        dispatch(logout());
      } else {
        dispatch(threadsResource.actions.addRequestError(err));
      }
      dispatch(toastError(`There was an error while adding`));
    }
  };

export default threadsResource;
